import { apiClient } from '@/lib/api/client'

/** Catalogue discovery (handlers/discovery-handler.js): demand gaps and external artists to fill them. */

export type CandidateStatus = 'proposed' | 'approved' | 'skipped' | 'snoozed'

export interface DiscoveryRun {
  id: number
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled'
  stage: 'demand' | 'topics' | 'candidates' | 'judge' | 'done'
  params: Record<string, unknown>
  stats: Record<string, unknown>
  error: string | null
  createdAt: string
  finishedAt: string | null
}

export interface TopQuery { q: string; impressions: number; clicks: number; position: number }

export interface DiscoveryWork {
  externalId: string
  title: string | null
  image: string | null
  grade?: number | string | null
  publishedAt?: string | null
  verdict?: { fit: number; personas: Record<string, number>; modern: number; quality: number; flags: string[]; reason: string }
  error?: string
}

export interface DiscoveryCandidate {
  id: number
  ucid: string
  provider: string
  externalArtistId: string
  artistName: string
  rank: number | null
  score: number
  components: {
    hits?: number
    quality?: number
    recency?: number
    catalogue?: number | null
    focus?: number
    country?: string | null
    aestheticFit?: number | null
    flags?: Record<string, number>
    disqualified?: boolean
    personas?: { name: string; score: number }[]
    amsArtistId?: number
    placeholderEmail?: string | null
  }
  works: DiscoveryWork[]
  reason: string | null
  status: CandidateStatus
  decidedAt: string | null
}

export interface DiscoveryTopic {
  ucid: string
  name: string
  category: string | null
  rank: number
  gapScore: number
  demand: { impressions: number; clicks: number; position: number | null; trend: number; units: number; topQueries: TopQuery[] }
  supply: { onSale: number; recentShare: number }
  searchTerms: string[]
  candidates: DiscoveryCandidate[]
}

export type DiscoveryRunDetail = DiscoveryRun & { topics: DiscoveryTopic[] }

const BASE = '/api/discovery'

export const discoveryApi = {
  getRuns: async (): Promise<{ data: DiscoveryRun[] }> => (await apiClient.get(`${BASE}/runs`)).data,
  getRun: async (id: number | 'latest'): Promise<{ data: DiscoveryRunDetail }> => (await apiClient.get(`${BASE}/runs/${id}`)).data,
  startRun: async (body: { topics?: number; skipDemand?: boolean } = {}): Promise<{ data: DiscoveryRun }> =>
    (await apiClient.post(`${BASE}/runs`, body)).data,
  cancelRun: async (id: number): Promise<{ data: DiscoveryRun }> => (await apiClient.post(`${BASE}/runs/${id}/cancel`)).data,
  decide: async (id: number, status: CandidateStatus): Promise<{ data: DiscoveryCandidate }> =>
    (await apiClient.patch(`${BASE}/candidates/${id}`, { status })).data,
}
